import {useCallback, useEffect, useRef, useState} from 'react';
import {acceptDojoToastEvent, dojoToastIdentityId, mergeDojoToast, shouldShowDojoToast} from './dojoCompletionToast';

/** Owns only transient Dojo announcements, never activity or durable note state. */
export function useDojoToasts(viewer) {
  const [pills, setPills] = useState([]);
  const viewerId = dojoToastIdentityId(viewer);
  const viewerRef = useRef(viewer);
  viewerRef.current = viewer;
  const previousViewerId = useRef(viewerId);
  const nextId = useRef(0);
  const history = useRef(new Map());
  const timers = useRef(new Map());
  const pending = useRef([]);

  const show = useCallback((message, activityId, user, activityType, event, opts = {}) => {
    if (event === 'completed' && !dojoToastIdentityId(viewerRef.current)) {
      // Initial hydration must not lose peer events, nor flash self praise.
      // Replay in arrival order once identity is known; existing revision and
      // priority deduplication then keeps the richest announcement per activity.
      pending.current = [...pending.current, [message, activityId, user, activityType, event, opts]].slice(-100);
      return;
    }
    const {coachHighlight = null, timerDuration = null, mediaFiles = []} = opts;
    const incoming = {id: ++nextId.current, message, activityId, user, activityType, event,
      coachHighlight, timerDuration, mediaFiles, exiting: false, ownerId: opts.ownerId,
      completionRevision: opts.completionRevision, completionTimeRevision: opts.completionTimeRevision,
      priority: opts.priority || 0};
    if (!shouldShowDojoToast(incoming, viewerRef.current)) return;
    if (!acceptDojoToastEvent(history.current, incoming)) return;
    const key = `${activityId}:${event}`;
    clearTimeout(timers.current.get(key));
    setPills(previous => mergeDojoToast(previous, incoming));
    timers.current.set(key, setTimeout(() => {
      setPills(previous => previous.map(pill => pill.id === incoming.id ? {...pill, exiting: true} : pill));
      timers.current.set(key, setTimeout(() => {
        setPills(previous => previous.filter(pill => pill.id !== incoming.id));
        timers.current.delete(key);
      }, 500));
    }, (timerDuration || 5) * 1000));
  }, []);

  useEffect(() => {
    if (previousViewerId.current && previousViewerId.current !== viewerId) {
      setPills([]);
      history.current.clear();
      pending.current = [];
      timers.current.forEach(timer => clearTimeout(timer));
      timers.current.clear();
    }
    previousViewerId.current = viewerId;
    if (viewerId) {
      const queued = pending.current;
      pending.current = [];
      queued.forEach(args => show(...args));
    }
  }, [viewerId, show]);

  useEffect(() => {
    const ownedTimers = timers.current;
    return () => {
      ownedTimers.forEach(timer => clearTimeout(timer));
      ownedTimers.clear();
      pending.current = [];
    };
  }, []);

  // Mask a changed identity synchronously, before scope cleanup effects run.
  const changingViewer = previousViewerId.current && previousViewerId.current !== viewerId;
  return {pills: changingViewer ? [] : pills.filter(pill => shouldShowDojoToast(pill, viewer)), show};
}
